import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import {
  DESCRIPTION_LIST_DENSITY,
  DESCRIPTION_LIST_DIVIDED,
  DESCRIPTION_LIST_FIRST_ROW,
  DESCRIPTION_LIST_LAYOUT,
  DescriptionListLayoutContext,
  type DescriptionListColumns,
  type DescriptionListDensity,
  type DescriptionListLayout,
} from "./descriptionListLayout.js";

export interface DescriptionListProps extends React.HTMLAttributes<HTMLDListElement> {
  /**
   * How each label meets its value.
   * - `stacked` (default): the label above the value.
   * - `horizontal`: the label beside the value, wrapping.
   * - `justified`: the label at the start, the value at the end.
   * - `grid`: labels and values aligned in auto/1fr columns.
   */
  layout?: DescriptionListLayout;
  /** Item columns from `sm` up; always one column below `sm`. Default 1. */
  columns?: DescriptionListColumns;
  /** Hairlines between rows. */
  divided?: boolean;
  /** The spacing between rows, nothing else. Default `comfortable`. */
  density?: DescriptionListDensity;
}

/* Label/value pairs as a native definition list (dl > div > dt + dd).
   Data-blind: compose `DescriptionItem` children; a dl allows only div, dt
   and dd children, so anything else fails axe `definition-list`. */
export const DescriptionList = forwardRef<
  HTMLDListElement,
  DescriptionListProps
>(function DescriptionList(
  {
    layout = "stacked",
    columns = 1,
    divided = false,
    density = "comfortable",
    className,
    children,
    ...rest
  },
  ref,
) {
  const classes = DESCRIPTION_LIST_LAYOUT[layout];
  const spacing = DESCRIPTION_LIST_DENSITY[density];
  return (
    <DescriptionListLayoutContext.Provider value={layout}>
      <dl
        ref={ref}
        className={cn(
          classes.list,
          classes.columns[columns],
          divided
            ? [
                DESCRIPTION_LIST_DIVIDED,
                DESCRIPTION_LIST_FIRST_ROW[columns],
                spacing.divided,
              ]
            : spacing.gap,
          className,
        )}
        {...rest}
      >
        {children}
      </dl>
    </DescriptionListLayoutContext.Provider>
  );
});
